import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/components/ui/carousel';
import { useForm } from 'react-hook-form';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { quoteService } from '@/services/QuoteService';
import { QuoteFormData, QuoteRequest, CustomerType, FundingMode } from '@/types/quote';
import { FileText, Loader2, Users, CalendarIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { CompanySearchInput } from '@/components/ui/CompanySearchInput';
import { CompanyData } from '@/hooks/useCompanyLookup';

interface QuoteRequestModalProps {
  courseId: string;
  courseTitle: string;
  coursePrice: number;
  courseDuration: number;
  courseDescription?: string;
  courseObjectives?: string;
  coursePrerequisites?: string;
  children?: React.ReactNode;
  trigger?: 'quote_from_product' | 'cart.export.quote';
  cartItems?: Array<{
    id: string;
    title: string;
    price: number;
    price_ht?: number;
    currency: string;
    duration_hours?: number;
  }>;
}


const LEGAL_FORMS = [
  { value: 'SARL', label: 'SARL' },
  { value: 'SAS', label: 'SAS' },
  { value: 'SASU', label: 'SASU' },
  { value: 'EURL', label: 'EURL' },
  { value: 'SA', label: 'SA' },
  { value: 'SNC', label: 'SNC' },
  { value: 'SCI', label: 'SCI' },
  { value: 'EI', label: 'Entreprise Individuelle' },
  { value: 'MICRO', label: 'Micro-entreprise' },
  { value: 'ASSOCIATION', label: 'Association' },
  { value: 'AUTRE', label: 'Autre' }
];

const COUNTRIES = [
  { value: 'FR', label: '🇫🇷 France' },
  { value: 'BE', label: '🇧🇪 Belgique' },
  { value: 'CH', label: '🇨🇭 Suisse' },
  { value: 'CA', label: '🇨🇦 Canada' },
  { value: 'LU', label: '🇱🇺 Luxembourg' },
  { value: 'MC', label: '🇲🇨 Monaco' },
  { value: 'MA', label: '🇲🇦 Maroc' },
  { value: 'TN', label: '🇹🇳 Tunisie' },
  { value: 'SN', label: '🇸🇳 Sénégal' },
  { value: 'CI', label: '🇨🇮 Côte d\'Ivoire' }
];

export default function QuoteRequestModal({
  courseId,
  courseTitle,
  coursePrice,
  courseDuration,
  courseDescription,
  courseObjectives,
  coursePrerequisites,
  children,
  trigger = 'quote_from_product',
  cartItems
}: QuoteRequestModalProps) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [courseData, setCourseData] = useState<any>(null);
  const [cartCoursesData, setCartCoursesData] = useState<any[]>([]);
  const { toast } = useToast();
  const { user } = useAuth();
  const navigate = useNavigate();

  // Vérification des rôles utilisateur
  const [userRole, setUserRole] = useState<string | null>(null);
  const isLearner = userRole === 'apprenant_particulier';
  const isAdminEntreprise = userRole === 'admin_entreprise';

  useEffect(() => {
    const checkUserRole = async () => {
      if (user) {
        try {
          const { data } = await supabase
            .from('user_roles')
            .select('role')
            .eq('user_id', user.id)
            .single();
          
          if (data) {
            setUserRole(data.role);
          }
        } catch (error) {
          console.error('Erreur vérification rôle:', error);
        }
      }
    };

    checkUserRole();
  }, [user]);

  const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm<QuoteFormData>({
    defaultValues: {
      customer_type: isAdminEntreprise ? 'company' : 'individual',
      participants_count: 1,
      funding_mode: 'VIREMENT',
      contact_first_name: '',
      contact_last_name: '',
      email: '',
      phone: '',
      company_name: '',
      siren: '',
      siret: '',
      legal_form: '',
      date_of_birth: '',
      address: '',
      postal_code: '',
      city: '',
      country_code: 'FR'
    }
  });

  const customerType = watch('customer_type');
  const fundingMode = watch('funding_mode');

  // Chargement des données de formation
  useEffect(() => {
    const loadCourseData = async () => {
      try {
        // Si c'est un export de panier, charger les données de toutes les formations du panier
        if (trigger === 'cart.export.quote' && cartItems && cartItems.length > 0) {
          const courseIds = cartItems.map(item => item.id);
          const { data: courses } = await supabase
            .from('courses')
            .select('*')
            .in('id', courseIds);
          
          if (courses) {
            setCartCoursesData(courses);
          }
        }
        
        // Charger la formation principale si courseId existe
        if (courseId) {
          const { data } = await supabase
            .from('courses')
            .select('*')
            .eq('id', courseId)
            .maybeSingle();
          
          if (data) {
            setCourseData(data);
          }
        }
      } catch (error) {
        console.error('Erreur chargement formation:', error);
      }
    };

    if (open) {
      loadCourseData();
    }
  }, [courseId, open, trigger, cartItems]);

  // Pré-remplissage des données utilisateur
  useEffect(() => {
    const prefillUserData = async () => {
      if (user?.email) {
        setValue('email', user.email);

        try {
          const { data: profile } = await supabase
            .from('profiles')
            .select('*')
            .eq('user_id', user.id)
            .single();

          if (profile) {
            if (profile.first_name) setValue('contact_first_name', profile.first_name);
            if (profile.last_name) setValue('contact_last_name', profile.last_name);
            if (profile.phone) setValue('phone', profile.phone);
            if (profile.company) setValue('company_name', profile.company);
            if (profile.siret) setValue('siret', profile.siret);
          }
        } catch (error) {
          console.error('Erreur chargement profil:', error);
        }
      }
    };

    if (open && user) {
      prefillUserData();
    }
  }, [open, user, setValue]);

  const onSubmit = async (data: QuoteFormData) => {
    try {
      setLoading(true);

      // Si OPCO est sélectionné, rediriger vers le module OPCO avec les données pré-remplies
      if (data.funding_mode === 'OPCO') {
        const formationData = {
          courseId,
          formationTitle: courseTitle,
          price: coursePrice,
          currency: 'EUR',
          duration: courseDuration
        };

        const companyData = {
          customer_type: isAdminEntreprise ? 'company' : (data.customer_type || 'individual'),
          nom: data.company_name,
          siren: data.siren || (data.siret ? data.siret.slice(0, 9) : ''),
          siret: data.siret || '',
          legal_form: data.legal_form,
          date_of_birth: data.date_of_birth,
          adresse: data.address,
          cp: data.postal_code,
          ville: data.city,
          country_code: data.country_code?.toUpperCase() || 'FR',
          prenom: data.contact_first_name,
          nom_contact: data.contact_last_name,
          email: data.email,
          telephone: data.phone || '',
          participants_count: data.participants_count || 1
        };

        setOpen(false);
        setLoading(false);
        
        navigate('/financement-opco', {
          state: {
            autoStart: true,
            formation: formationData,
            companyData
          }
        });
        return;
      }

      // Validation spécifique selon le type de client pour les autres modes de financement
      if (data.customer_type === 'company' || isAdminEntreprise) {
        if (!data.company_name || !data.siren) {
          toast({
            title: "Erreur de validation",
            description: "Le nom d'entreprise et SIREN sont requis pour les entreprises.",
            variant: "destructive"
          });
          setLoading(false);
          return;
        }
      }

      if (data.customer_type === 'individual' && !data.date_of_birth) {
        toast({
          title: "Erreur de validation",
          description: "La date de naissance est requise pour les particuliers.",
          variant: "destructive"
        });
        setLoading(false);
        return;
      }

      // Validation des champs d'adresse
      if (!data.address || !data.postal_code || !data.city || !data.country_code) {
        toast({
          title: "Erreur de validation",
          description: "Tous les champs d'adresse sont requis.",
          variant: "destructive"
        });
        setLoading(false);
        return;
      }

      // Dériver SIREN depuis SIRET si nécessaire
      const finalSiren = data.siren || (data.siret ? data.siret.slice(0, 9) : '');

      // Calculer les totaux
      let subtotalHT = 0;
      let items = [];

      if (trigger === 'cart.export.quote' && cartItems) {
        // Mode panier - envoyer chaque item comme entrée séparée
        items = cartItems.map((item, index) => {
          const unitPriceHT = item.price_ht || (item.price / 1.2);
          subtotalHT += unitPriceHT;
          return {
            sku: item.id,
            title: item.title,
            qty: 1,
            unit_price_ht: unitPriceHT,
            tax_rate: 0.2,
            // Forcer un identifiant unique pour chaque ligne
            line_id: `line_${index + 1}`,
            product_code: item.id,
            description: item.title,
            category: "formation"
          };
        });
      } else {
        // Mode cours unique
        const unitPriceHT = coursePrice / 1.2; // TVA 20%
        subtotalHT = unitPriceHT * data.participants_count;
        items = [{
          sku: courseId,
          title: courseTitle,
          qty: data.participants_count,
          unit_price_ht: unitPriceHT,
          tax_rate: 0.2,
          line_id: "line_1",
          product_code: courseId,
          description: courseTitle,
          category: "formation"
        }];
      }

      const taxTotal = subtotalHT * 0.2;
      const totalTTC = subtotalHT + taxTotal;

      // Debug: afficher les items dans la console
      console.log('[DEBUG] Items envoyés au webhook:', items);

      const payload: QuoteRequest = {
        trigger,
        customer: {
          type: isAdminEntreprise ? 'company' : (data.customer_type || 'individual'),
          company_name: data.company_name,
          siren: finalSiren,
          siret: data.siret || '',
          legal_form: data.legal_form,
          date_of_birth: data.date_of_birth,
          address: data.address,
          postal_code: data.postal_code,
          city: data.city,
          country_code: data.country_code?.toUpperCase() || 'FR',
          contact_first_name: data.contact_first_name,
          contact_last_name: data.contact_last_name,
          email: data.email,
          phone: data.phone || ''
        },
        funding: {
          mode: data.funding_mode
        },
        training: {
          id: trigger === 'cart.export.quote' ? 'cart' : courseId,
          title: trigger === 'cart.export.quote' ? 
            (cartItems?.map(item => item.title).join(', ') || courseTitle) : courseTitle,
          description: courseDescription || '',
          objectives: courseObjectives || '',
          prerequisites: coursePrerequisites || '',
          duration_hours: trigger === 'cart.export.quote' 
            ? cartItems?.reduce((total, item) => total + (item.duration_hours || 0), 0) || courseDuration || 4
            : courseDuration,
          modality: 'distanciel'
        },
        items,
        totals: {
          subtotal_ht: subtotalHT,
          tax_total: taxTotal,
          total_ttc: totalTTC,
          currency: 'EUR'
        },
        meta: {
          source: 'site',
          locale: 'fr-FR',
          cart_id: trigger === 'cart.export.quote' ? 'cart_' + Date.now() : undefined,
          participants_count: data.participants_count || 1,
          fundingMode: data.funding_mode || 'VIREMENT',
          timestamp: new Date().toISOString()
        },
        redirect_urls: {
          success_url: `${window.location.origin}/devis/{quoteId}`,
          signature_return_url: `${window.location.origin}/devis/{quoteId}?signed=1`
        }
      };

      const response = await quoteService.createQuote(payload);
      
      toast({
        title: "Demande envoyée !",
        description: "Votre demande de devis a été envoyée. Vous recevrez votre devis par email."
      });
      
      setOpen(false);
      navigate(`/devis/${response.quote_id}`);
      
    } catch (error) {
      console.error('Erreur création devis:', error);
      toast({
        title: "Erreur",
        description: "Une erreur est survenue lors de l'envoi de votre demande.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {children || (
          <Button>
            <FileText className="h-4 w-4 mr-2" />
            Demander un devis
          </Button>
        )}
      </DialogTrigger>
      
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Demande de devis</DialogTitle>
          <DialogDescription>
            Remplissez ce formulaire pour recevoir votre devis personnalisé
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Formations dans le panier ou formation sélectionnée */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                {trigger === 'cart.export.quote' && cartItems && cartItems.length > 1 
                  ? `Formations sélectionnées (${cartItems.length})` 
                  : 'Formation sélectionnée'
                }
              </CardTitle>
            </CardHeader>
            <CardContent>
              {trigger === 'cart.export.quote' && cartItems && cartItems.length > 1 ? (
                // Carrousel pour plusieurs formations dans le panier
                <Carousel className="w-full">
                  <CarouselContent>
                    {cartItems.map((item) => {
                      const courseInfo = cartCoursesData.find(course => course.id === item.id);
                      return (
                        <CarouselItem key={item.id}>
                          <div className="p-4 border rounded-lg">
                            <div className="space-y-3">
                              <div>
                                <Label className="font-medium">Intitulé :</Label>
                                <p className="text-sm text-muted-foreground mt-1 font-medium">
                                  {courseInfo?.title || item.title}
                                </p>
                              </div>
                              <div>
                                <Label className="font-medium">Objectifs :</Label>
                                <div className="text-sm text-muted-foreground mt-1">
                                  {courseInfo?.metadata?.learning_objectives || courseInfo?.metadata?.outcomes ? (
                                    <ul className="list-disc list-inside space-y-1">
                                      {(courseInfo.metadata.learning_objectives || courseInfo.metadata.outcomes).map((objective: string, index: number) => (
                                        <li key={index}>{objective}</li>
                                      ))}
                                    </ul>
                                  ) : (
                                    <p>Objectifs non spécifiés</p>
                                  )}
                                </div>
                              </div>
                              <div>
                                <Label className="font-medium">Description :</Label>
                                <p className="text-sm text-muted-foreground mt-1">
                                  {courseInfo?.description || "Description non disponible"}
                                </p>
                              </div>
                              <div>
                                <Label className="font-medium">Prérequis :</Label>
                                <p className="text-sm text-muted-foreground mt-1">
                                  {courseInfo?.metadata?.target_audience || "Aucun prérequis spécifique"}
                                </p>
                              </div>
                              <div>
                                <Label className="font-medium">Prix :</Label>
                                <p className="text-sm text-muted-foreground mt-1">
                                  {new Intl.NumberFormat('fr-FR', {
                                    style: 'currency',
                                    currency: item.currency
                                  }).format(item.price)}
                                </p>
                              </div>
                            </div>
                          </div>
                        </CarouselItem>
                      );
                    })}
                  </CarouselContent>
                  <CarouselPrevious />
                  <CarouselNext />
                </Carousel>
              ) : (
                // Affichage simple pour une formation
                <div className="space-y-3">
                  <div>
                    <Label className="font-medium">Intitulé :</Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      {courseData?.title || courseTitle}
                    </p>
                  </div>
                  <div>
                    <Label className="font-medium">Objectifs :</Label>
                    <div className="text-sm text-muted-foreground mt-1">
                      {courseData?.metadata?.learning_objectives || courseData?.metadata?.outcomes ? (
                        <ul className="list-disc list-inside space-y-1">
                          {(courseData.metadata.learning_objectives || courseData.metadata.outcomes).map((objective: string, index: number) => (
                            <li key={index}>{objective}</li>
                          ))}
                        </ul>
                      ) : courseObjectives ? (
                        <ul className="list-disc list-inside">
                          <li>{courseObjectives}</li>
                        </ul>
                      ) : (
                        <p>Objectifs non spécifiés</p>
                      )}
                    </div>
                  </div>
                  <div>
                    <Label className="font-medium">Description :</Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      {courseData?.description || courseDescription || "Description non disponible"}
                    </p>
                  </div>
                  <div>
                    <Label className="font-medium">Prérequis :</Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      {courseData?.metadata?.target_audience || coursePrerequisites || "Aucun prérequis spécifique"}
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Type de client */}
          {!isLearner && !isAdminEntreprise && (
            <div className="space-y-3">
              <Label>Type de client</Label>
              <RadioGroup 
                value={customerType} 
                onValueChange={(value: CustomerType) => setValue('customer_type', value)} 
                className="flex gap-6"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="company" id="company" />
                  <Label htmlFor="company">Entreprise</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="individual" id="individual" />
                  <Label htmlFor="individual">Particulier</Label>
                </div>
              </RadioGroup>
            </div>
          )}

          {/* Informations entreprise */}
          {(customerType === 'company' || isAdminEntreprise) && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="company_name">Nom de l'entreprise *</Label>
                <Input 
                  id="company_name" 
                  {...register('company_name', {
                    required: customerType === 'company' || isAdminEntreprise
                  })} 
                  placeholder="ex. ACME SAS" 
                />
                {errors.company_name && <span className="text-sm text-red-500">Ce champ est requis</span>}
              </div>

              <div>
                <Label htmlFor="legal_form">Forme juridique *</Label>
                <Select onValueChange={value => setValue('legal_form', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner la forme juridique" />
                  </SelectTrigger>
                  <SelectContent>
                    {LEGAL_FORMS.map(form => (
                      <SelectItem key={form.value} value={form.value}>
                        {form.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <CompanySearchInput
                value={watch('siren') || ''}
                onChange={(value) => {
                  setValue('siren', value.slice(0, 9));
                  if (value.length === 14) {
                    setValue('siret', value);
                  }
                }}
                onCompanyFound={(data: CompanyData) => {
                  setValue('company_name', data.denomination);
                  setValue('siren', data.siren);
                  setValue('siret', data.siret);
                  setValue('address', data.adresse);
                  setValue('postal_code', data.code_postal);
                  setValue('city', data.ville);
                }}
                onClear={() => {
                  setValue('company_name', '');
                  setValue('siren', '');
                  setValue('siret', '');
                  setValue('address', '');
                  setValue('postal_code', '');
                  setValue('city', '');
                }}
                showCompanyPreview={true}
              />
              
              <div>
                <Label htmlFor="siret">SIRET complet</Label>
                <Input 
                  id="siret" 
                  {...register('siret', {
                    pattern: {
                      value: /^\d{14}$/,
                      message: "Le SIRET doit contenir 14 chiffres."
                    }
                  })} 
                  placeholder="Auto-rempli depuis SIREN" 
                />
                {errors.siret && <span className="text-sm text-red-500">{errors.siret.message}</span>}
              </div>
            </div>
          )}

          {/* Contact - Prénom et Nom */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="contact_first_name">Prénom *</Label>
              <Input 
                id="contact_first_name" 
                {...register('contact_first_name', { required: true })} 
              />
              {errors.contact_first_name && <span className="text-sm text-red-500">Ce champ est requis</span>}
            </div>
            <div>
              <Label htmlFor="contact_last_name">Nom *</Label>
              <Input 
                id="contact_last_name" 
                {...register('contact_last_name', { required: true })} 
              />
              {errors.contact_last_name && <span className="text-sm text-red-500">Ce champ est requis</span>}
            </div>
          </div>

          {/* Date de naissance pour les particuliers */}
          {(customerType === 'individual' || isLearner) && (
            <div>
              <Label>Date de naissance *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !watch('date_of_birth') && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {watch('date_of_birth') ? (
                      format(new Date(watch('date_of_birth')), "d MMMM yyyy", { locale: fr })
                    ) : (
                      <span>Sélectionner une date</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={watch('date_of_birth') ? new Date(watch('date_of_birth') + 'T00:00:00') : undefined}
                    onSelect={(date) => {
                      if (date) {
                        const year = date.getFullYear();
                        const month = String(date.getMonth() + 1).padStart(2, '0');
                        const day = String(date.getDate()).padStart(2, '0');
                        setValue('date_of_birth', `${year}-${month}-${day}`);
                      }
                    }}
                    disabled={(date) => date > new Date()}
                    initialFocus
                    locale={fr}
                    captionLayout="dropdown-buttons"
                    fromYear={1900}
                    toYear={new Date().getFullYear()}
                    labels={{
                      labelMonthDropdown: () => "Mois",
                      labelYearDropdown: () => "Année"
                    }}
                    className="pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
              {errors.date_of_birth && <span className="text-sm text-red-500">Ce champ est requis</span>}
            </div>
          )}

          {/* Adresse pour tous */}
          <div className="space-y-4">
            <div>
              <Label htmlFor="address">Adresse *</Label>
              <Input 
                id="address" 
                {...register('address', { required: true })} 
                placeholder="ex. 10 rue de Paris" 
              />
              {errors.address && <span className="text-sm text-red-500">Ce champ est requis</span>}
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="postal_code">Code postal *</Label>
                <Input 
                  id="postal_code" 
                  {...register('postal_code', {
                    required: true,
                    pattern: watch('country_code') === 'FR' ? {
                      value: /^\d{5}$/,
                      message: "Code postal français invalide (5 chiffres)"
                    } : undefined
                  })} 
                  placeholder="ex. 75001" 
                  maxLength={10} 
                />
                {errors.postal_code && <span className="text-sm text-red-500">{errors.postal_code.message || "Ce champ est requis"}</span>}
              </div>
              <div>
                <Label htmlFor="city">Ville *</Label>
                <Input 
                  id="city" 
                  {...register('city', { required: true })} 
                  placeholder="ex. Paris" 
                />
                {errors.city && <span className="text-sm text-red-500">Ce champ est requis</span>}
              </div>
            </div>
            
            <div>
              <Label htmlFor="country_code">Pays *</Label>
              <Select onValueChange={value => setValue('country_code', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner un pays" />
                </SelectTrigger>
                <SelectContent>
                  {COUNTRIES.map(country => (
                    <SelectItem key={country.value} value={country.value}>
                      {country.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.country_code && <span className="text-sm text-red-500">Ce champ est requis</span>}
            </div>
          </div>

          {/* Email et téléphone */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="email">Adresse email *</Label>
              <Input 
                id="email" 
                type="email" 
                {...register('email', {
                  required: true,
                  pattern: /^\S+@\S+$/i
                })} 
              />
              {errors.email && <span className="text-sm text-red-500">Email valide requis</span>}
            </div>
            <div>
              <Label htmlFor="phone">Téléphone</Label>
              <Input id="phone" type="tel" {...register('phone')} />
            </div>
          </div>

          {/* Participants - uniquement pour les entreprises */}
          {trigger !== 'cart.export.quote' && (customerType === 'company' || isAdminEntreprise) && (
            <div>
              <Label htmlFor="participants_count">Nombre de participants</Label>
              <Select onValueChange={value => setValue('participants_count', parseInt(value))}>
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner" />
                </SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                    <SelectItem key={num} value={num.toString()}>{num}</SelectItem>
                  ))}
                  <SelectItem value="11">Plus de 10</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Mode de financement */}
          <div>
            <Label htmlFor="funding_mode">Mode de financement *</Label>
            <Select onValueChange={(value: FundingMode) => setValue('funding_mode', value)}>
              <SelectTrigger>
                <SelectValue placeholder="Sélectionner" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="OPCO">OPCO</SelectItem>
                <SelectItem value="VIREMENT">Virement bancaire</SelectItem>
              </SelectContent>
            </Select>
          </div>


          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Annuler
            </Button>
            <Button type="submit" disabled={loading} className="flex-1">
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {watch('funding_mode') === 'OPCO' ? 'Continuer avec OPCO' : 'Envoyer la demande de devis'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}